import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, User, Save, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader } from "../../components/dashboard/Shared";

const EXPERIENCE = [
  ["none", "No experience"],
  ["beginner", "Beginner (< 1 year)"],
  ["intermediate", "Intermediate (1-3 years)"],
  ["advanced", "Advanced (3-5 years)"],
  ["professional", "Professional (5+ years)"],
];
const APPETITE = [
  ["low", "Low — conservative growth"],
  ["medium", "Medium — balanced"],
  ["high", "High — aggressive"],
];

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [elig, setElig] = useState(null);

  useEffect(() => {
    api.get("/investor/mt5").then(({ data }) => setElig(data.eligibility)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => {
      setForm({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        phone: data.phone || "",
        country: data.country || "",
        profession: data.profession || "",
        trading_experience: data.trading_experience || "beginner",
        current_equity: data.current_equity_declared ?? "",
        risk_appetite: data.risk_appetite || "medium",
      });
    }).catch(() => setForm({
      first_name: "", last_name: "", phone: "", country: "", profession: "",
      trading_experience: "beginner", current_equity: "", risk_appetite: "medium",
    }));
  }, []);

  const bind = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.current_equity === "") delete payload.current_equity;
      else payload.current_equity = parseFloat(payload.current_equity) || 0;
      const { data } = await api.put("/auth/profile", payload);
      setUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update profile");
    } finally { setBusy(false); }
  };

  if (!form) return <div className="skeleton h-80" />;

  return (
    <div className="max-w-2xl">
      <PageHeader title="My Profile" sub="Manage your personal and trading details" />
      <div className="card-dark p-5 mb-6 flex items-center gap-4" data-testid="profile-identity-card">
        <div className="w-12 h-12 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center shrink-0">
          <User size={20} className="text-[#00F0FF]" />
        </div>
        <div className="min-w-0">
          <p className="text-white truncate" data-testid="profile-name">{user?.name || "—"}</p>
          <p className="text-xs text-gray-500 truncate" data-testid="profile-email">{user?.email}</p>
        </div>
      </div>

      {elig && (
        <div
          data-testid="profile-eligibility-badge"
          className={`card-dark p-4 mb-6 flex items-start gap-3 ${elig.eligible ? "border-[#00E676]/30" : "border-[#FFD600]/40"}`}
        >
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${elig.eligible ? "bg-[#00E676]/15 border border-[#00E676]/30" : "bg-[#FFD600]/15 border border-[#FFD600]/30"}`}>
            {elig.eligible ? <CheckCircle2 size={18} className="text-[#00E676]" /> : <AlertTriangle size={18} className="text-[#FFD600]" />}
          </div>
          <div className="min-w-0">
            <p className="text-sm text-white font-medium" data-testid="profile-eligibility-status">{elig.eligible ? "Eligible for copy-trading" : "Not yet eligible"}</p>
            <p className="text-xs text-gray-400 leading-relaxed mt-0.5">{elig.reason}. Minimum ${Number(elig.min_equity).toLocaleString()} account equity is required.</p>
            <div className="flex gap-4 mt-2 text-[11px] text-gray-500">
              <span>Declared: <span className="text-gray-300">{elig.declared_equity == null ? "—" : `$${Number(elig.declared_equity).toLocaleString()}`}</span></span>
              <span>Broker-verified: <span className="text-gray-300">{elig.verified_equity == null ? "—" : `$${Number(elig.verified_equity).toLocaleString()}`}</span></span>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={save} className="card-dark p-6 space-y-4" data-testid="profile-form">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-xs block mb-1">First Name</label><input data-testid="profile-first-name" className="input-dark" {...bind("first_name")} /></div>
          <div><label className="label-xs block mb-1">Last Name</label><input data-testid="profile-last-name" className="input-dark" {...bind("last_name")} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-xs block mb-1">Phone (with country code)</label><input data-testid="profile-phone" className="input-dark" placeholder="+91 98765 43210" {...bind("phone")} /></div>
          <div><label className="label-xs block mb-1">Country</label><input data-testid="profile-country" className="input-dark" {...bind("country")} /></div>
        </div>
        <div><label className="label-xs block mb-1">Profession</label><input data-testid="profile-profession" className="input-dark" placeholder="Software engineer" {...bind("profession")} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-xs block mb-1">Trading Experience</label>
            <select data-testid="profile-experience" className="input-dark" {...bind("trading_experience")}>
              {EXPERIENCE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="label-xs block mb-1">Risk Appetite</label>
            <select data-testid="profile-appetite" className="input-dark" {...bind("risk_appetite")}>
              {APPETITE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="label-xs block mb-1">Current Trading Equity (USD)</label>
          <input data-testid="profile-equity" type="number" min="0" step="1" className="input-dark" placeholder="e.g. 10000" {...bind("current_equity")} />
          <p className="text-[11px] text-gray-600 mt-1">Used for ChartzFx eligibility review (minimum USD $1,000).</p>
        </div>
        <button data-testid="profile-save-button" disabled={busy} className="btn-primary px-6 py-2.5 text-sm font-medium disabled:opacity-50 flex items-center gap-2">
          {busy ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save Changes</>}
        </button>
      </form>
    </div>
  );
}
